/**
 * Wall3DView — 3D viewer for a reinforced concrete structural wall (placa).
 *
 * Geometry:
 *   - Concrete: BoxGeometry of (lw × hw × tw) extruded vertically (y is up).
 *   - Vertical bars: cylinders along the +y axis (the wall height).
 *       · Boundary element (BE) bars at each end (lbe wide) — concentrated.
 *       · Distributed mesh between the BE regions in 1 or 2 curtains.
 *   - Horizontal bars (mesh): cylinders running along x (the wall length),
 *     stacked vertically every s_h cm at each face (n_layers curtains).
 *   - BE confining stirrups: closed rectangles around the BE bars at each
 *     stirrup level along the height.
 *
 * Scene axes:
 *   x : along the wall length (lw)
 *   y : vertical (hw)
 *   z : across the thickness (tw)
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525,
  '1/2': 1.27,
  '5/8': 1.5875,
  '3/4': 1.905,
  '1': 2.54,
  '1 3/8': 3.4925,
};

const COLOR_CONCRETE = '#94A3B8';
const COLOR_CONCRETE_EDGE = '#94A3B8';
const COLOR_BAR_BE = '#F59E0B';        // boundary-element vertical bars (amber)
const COLOR_BAR_MESH_V = '#8B5CF6';    // distributed vertical mesh (violet)
const COLOR_BAR_MESH_H = '#22C55E';    // horizontal mesh (green)
const COLOR_STIRRUP = '#0891B2';       // BE confining stirrups (cyan)

interface Reinforcement {
  diam_be: string;
  n_be_cols: number;
  n_be_rows: number;
  diam_v: string;
  s_v: number;
  n_layers: number;
  diam_h: string;
  s_h: number;
  diam_stirrup_be: string;
  s_stirrup_be: number;
}

interface Props {
  lw: number;
  tw: number;
  hw: number;
  lbe: number;
  cover: number;
  reinforcement: Reinforcement;
}

export function Wall3DView({ lw, tw, hw, lbe, cover, reinforcement }: Props) {
  // Scale cm → scene units so the longest dimension is ~5 units.
  const s = Math.min(5 / Math.max(lw, hw), 2.5 / Math.max(tw, 1));

  const Lx = lw * s;
  const Ly = hw * s;
  const Lz = tw * s;

  const d_be = DIAM_INCH_TO_CM[reinforcement.diam_be] ?? 1.5875;
  const d_v = DIAM_INCH_TO_CM[reinforcement.diam_v] ?? 0.9525;
  const d_h = DIAM_INCH_TO_CM[reinforcement.diam_h] ?? 0.9525;
  const d_stirrup = DIAM_INCH_TO_CM[reinforcement.diam_stirrup_be] ?? 0.9525;

  const off_be = cover + d_stirrup + d_be / 2;
  const off_v = cover + d_stirrup + d_v / 2;

  // ---- BE bar positions (x, z) in cm relative to wall center ----
  const beXLocal = useMemo(() => {
    if (reinforcement.n_be_cols === 1) return [0];
    const usable = lbe - 2 * off_be;
    const step = usable / (reinforcement.n_be_cols - 1);
    return Array.from({ length: reinforcement.n_be_cols }, (_, i) => -lbe / 2 + off_be + i * step);
  }, [reinforcement.n_be_cols, lbe, off_be]);

  const beZs = useMemo(() => {
    if (reinforcement.n_be_rows === 1) return [0];
    if (reinforcement.n_be_rows === 2) return [-(tw / 2 - off_be), +(tw / 2 - off_be)];
    const usable = tw - 2 * off_be;
    return Array.from({ length: reinforcement.n_be_rows }, (_, i) =>
      -(tw / 2 - off_be) + (i * usable) / (reinforcement.n_be_rows - 1),
    );
  }, [reinforcement.n_be_rows, tw, off_be]);

  // ---- Mesh vertical bar positions ----
  const middleLen = Math.max(lw - 2 * lbe, 0);
  const meshCount = middleLen > 0 ? Math.floor(middleLen / reinforcement.s_v) + 1 : 0;
  const meshXs = useMemo(() => {
    if (meshCount <= 0) return [];
    const start = -lw / 2 + lbe + (middleLen - (meshCount - 1) * reinforcement.s_v) / 2;
    return Array.from({ length: meshCount }, (_, i) => start + i * reinforcement.s_v);
  }, [meshCount, middleLen, lw, lbe, reinforcement.s_v]);

  const meshZs = useMemo(() => {
    if (reinforcement.n_layers === 1) return [0];
    return [-(tw / 2 - off_v), +(tw / 2 - off_v)];
  }, [reinforcement.n_layers, tw, off_v]);

  // ---- Horizontal bar Y positions ----
  const horizYs = useMemo(() => {
    const ys: number[] = [];
    let y = reinforcement.s_h;
    while (y < hw) {
      ys.push(y);
      y += reinforcement.s_h;
    }
    return ys;
  }, [reinforcement.s_h, hw]);

  // ---- Stirrup Y positions along the height (BE only) ----
  const stirrupYs = useMemo(() => {
    const ys: number[] = [];
    let y = reinforcement.s_stirrup_be / 2;
    while (y < hw) {
      ys.push(y);
      y += reinforcement.s_stirrup_be;
    }
    return ys;
  }, [reinforcement.s_stirrup_be, hw]);

  // ---- Stirrup loop geometry per BE (closed rectangle in XZ plane) ----
  const stirrupGeoms = useMemo(() => {
    // Center the loop on the BE rectangle. Coords in cm → scene units via `s`.
    const halfLbeIn = (lbe / 2 - cover - d_stirrup / 2) * s;
    const halfTwIn = (tw / 2 - cover - d_stirrup / 2) * s;
    const pts = [
      new THREE.Vector3(-halfLbeIn, 0, -halfTwIn),
      new THREE.Vector3(+halfLbeIn, 0, -halfTwIn),
      new THREE.Vector3(+halfLbeIn, 0, +halfTwIn),
      new THREE.Vector3(-halfLbeIn, 0, +halfTwIn),
      new THREE.Vector3(-halfLbeIn, 0, -halfTwIn),
    ];
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [lbe, tw, cover, d_stirrup, s]);

  // BE centers along x
  const beLeftCenterX = -(lw / 2 - lbe / 2);
  const beRightCenterX = +(lw / 2 - lbe / 2);

  // Y in scene units (centered at hw/2 → translate so floor is at y=0)
  const yOfFloor = -Ly / 2;
  const yScene = (h_cm: number) => yOfFloor + h_cm * s;

  // Bar radii in scene units
  const r_be = (d_be / 2) * s;
  const r_v = (d_v / 2) * s;
  const r_h = (d_h / 2) * s;

  return (
    <Canvas
      camera={{ position: [7, 5, 7], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid
        args={[20, 20]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={14}
        fadeStrength={1.2}
        position={[0, yOfFloor - 0.01, 0]}
        infiniteGrid
      />

      <Bounds fit clip observe margin={1.4}>
        {/* CONCRETE WALL */}
        <mesh>
          <boxGeometry args={[Lx, Ly, Lz]} />
          <meshStandardMaterial
            color={COLOR_CONCRETE}
            transparent
            opacity={0.32}
            roughness={0.85}
            metalness={0.05}
          />
        </mesh>
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(Lx, Ly, Lz)]} />
          <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
        </lineSegments>

        {/* BE bars at each end (vertical cylinders along +y) */}
        {[beLeftCenterX, beRightCenterX].map((cx, side) => (
          <group key={`be-${side}`}>
            {beXLocal.map((xLoc, i) =>
              beZs.map((z, j) => (
                <Cylinder
                  key={`be-${side}-${i}-${j}`}
                  radius={r_be}
                  length={Ly}
                  position={[(cx + xLoc) * s, 0, z * s]}
                  rotation={[0, 0, 0]}
                  color={COLOR_BAR_BE}
                />
              )),
            )}
          </group>
        ))}

        {/* Mesh vertical bars (between BEs) */}
        {meshXs.map((x, i) =>
          meshZs.map((z, j) => (
            <Cylinder
              key={`mesh-v-${i}-${j}`}
              radius={r_v}
              length={Ly}
              position={[x * s, 0, z * s]}
              rotation={[0, 0, 0]}
              color={COLOR_BAR_MESH_V}
            />
          )),
        )}

        {/* Horizontal mesh bars (along x, stacked along y in 2 curtains) */}
        {horizYs.map((hCm, i) =>
          meshZs.map((z, j) => (
            <Cylinder
              key={`mesh-h-${i}-${j}`}
              radius={r_h}
              length={Lx}
              position={[0, yScene(hCm), z * s]}
              rotation={[0, 0, Math.PI / 2]}
              color={COLOR_BAR_MESH_H}
            />
          )),
        )}

        {/* BE confining stirrups — rectangular loops at each level */}
        {[beLeftCenterX, beRightCenterX].map((cx, side) =>
          stirrupYs.map((hCm, i) => (
            <line key={`stirr-${side}-${i}`} position={[cx * s, yScene(hCm), 0]}>
              <primitive attach="geometry" object={stirrupGeoms} />
              <lineBasicMaterial color={COLOR_STIRRUP} />
            </line>
          )),
        )}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

function Cylinder({
  radius,
  length,
  position,
  rotation,
  color,
}: {
  radius: number;
  length: number;
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
}) {
  const geom = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, length, 14),
    [radius, length],
  );
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
